import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Search, Mail, Phone, Trash2, Link2, X } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type ParentRow = Database["public"]["Tables"]["parents"]["Row"];
type LearnerLite = { id: string; first_name: string; last_name: string; admission_number: string | null };
type Link = { id: string; learner_id: string; relationship: Database["public"]["Enums"]["parent_relation"]; is_primary: boolean; is_emergency_contact: boolean; learner: LearnerLite | null };

export const Route = createFileRoute("/admin/parents")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Parents — MAKAI" }] }),
  component: ParentsPage,
});

function ParentsPage() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<ParentRow[]>([]);
  const [links, setLinks] = useState<Record<string, Link[]>>({});
  const [learners, setLearners] = useState<LearnerLite[]>([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!active) return;
    const [{ data: p }, { data: pll }, { data: ll }] = await Promise.all([
      supabase.from("parents").select("*").eq("school_id", active.school_id).order("last_name"),
      supabase.from("parent_learner_links").select("id,parent_id,learner_id,relationship,is_primary,is_emergency_contact, learner:learners(id,first_name,last_name,admission_number)").eq("school_id", active.school_id),
      supabase.from("learners").select("id,first_name,last_name,admission_number").eq("school_id", active.school_id).eq("status","active").order("last_name"),
    ]);
    setRows((p ?? []) as ParentRow[]);
    const map: Record<string, Link[]> = {};
    (pll ?? []).forEach((r: any) => { (map[r.parent_id] ??= []).push({ id: r.id, learner_id: r.learner_id, relationship: r.relationship, is_primary: r.is_primary, is_emergency_contact: r.is_emergency_contact, learner: r.learner }); });
    setLinks(map);
    setLearners((ll ?? []) as LearnerLite[]);
  }, [active]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => !q || `${r.first_name} ${r.last_name} ${r.phone} ${r.email ?? ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AdminShell title="Parents & guardians" subtitle="Profiles, contact info and the children they're linked to." action={<ParentDialog onSaved={load} />}>
      <div className="mb-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parents" className="pl-9" />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No parents yet. Add one and link them to their children.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-display text-base font-bold">{p.first_name} {p.last_name}</div>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</span>}
                    {p.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</span>}
                  </div>
                  {p.occupation && <div className="mt-1 text-xs text-muted-foreground">{p.occupation}</div>}
                </div>
                <div className="flex gap-1">
                  <LinkChildDialog parentId={p.id} learners={learners} onSaved={load} />
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                    if (!confirm("Delete this parent record?")) return;
                    const { error } = await supabase.from("parents").delete().eq("id", p.id);
                    if (error) toast.error(error.message); else load();
                  }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {(links[p.id] ?? []).length === 0 && <div className="text-xs italic text-muted-foreground">No children linked yet.</div>}
                {(links[p.id] ?? []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-1.5 text-xs">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{l.learner ? `${l.learner.first_name} ${l.learner.last_name}` : "Unknown learner"}</div>
                      <div className="text-[11px] capitalize text-muted-foreground">
                        {l.relationship}{l.is_primary && " · primary"}{l.is_emergency_contact && " · emergency"}
                      </div>
                    </div>
                    <button onClick={async () => {
                      const { error } = await supabase.from("parent_learner_links").delete().eq("id", l.id);
                      if (error) toast.error(error.message); else load();
                    }} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}

function ParentDialog({ onSaved }: { onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", alt_phone: "", email: "", occupation: "", address: "", national_id: "" });
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.first_name || !form.last_name || !form.phone) { toast.error("Name and phone required"); return; }
    setBusy(true);
    const { error } = await supabase.from("parents").insert({
      school_id: active.school_id,
      first_name: form.first_name, last_name: form.last_name,
      phone: form.phone, alt_phone: form.alt_phone || null, email: form.email || null,
      occupation: form.occupation || null, address: form.address || null, national_id: form.national_id || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Parent added"); setOpen(false); onSaved();
    setForm({ first_name: "", last_name: "", phone: "", alt_phone: "", email: "", occupation: "", address: "", national_id: "" });
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Add parent</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New parent / guardian</DialogTitle></DialogHeader>
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
          <Field label="First name *"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></Field>
          <Field label="Last name *"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></Field>
          <Field label="Phone *"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></Field>
          <Field label="Alt phone"><Input value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} /></Field>
          <Field label="Email" className="sm:col-span-2"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Occupation"><Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></Field>
          <Field label="National ID"><Input value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} /></Field>
          <Field label="Address" className="sm:col-span-2"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <DialogFooter className="sm:col-span-2"><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LinkChildDialog({ parentId, learners, onSaved }: { parentId: string; learners: LearnerLite[]; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<{ learner_id: string; relationship: Database["public"]["Enums"]["parent_relation"]; is_primary: boolean; is_emergency_contact: boolean }>({ learner_id: "", relationship: "guardian", is_primary: false, is_emergency_contact: true });
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.learner_id) { toast.error("Pick a learner"); return; }
    setBusy(true);
    const { error } = await supabase.from("parent_learner_links").insert({ school_id: active.school_id, parent_id: parentId, ...form });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Linked"); setOpen(false); onSaved();
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline"><Link2 className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Link to child</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Learner *">
            <Select value={form.learner_id} onValueChange={(v) => setForm({ ...form, learner_id: v })}>
              <SelectTrigger><SelectValue placeholder="Pick a learner" /></SelectTrigger>
              <SelectContent>{learners.map((l) => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}{l.admission_number ? ` (${l.admission_number})` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Relationship">
            <Select value={form.relationship} onValueChange={(v) => setForm({ ...form, relationship: v as typeof form.relationship })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["mother","father","guardian","sibling","other"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Primary contact</Label><Switch checked={form.is_primary} onCheckedChange={(v) => setForm({ ...form, is_primary: v })} /></div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Emergency contact</Label><Switch checked={form.is_emergency_contact} onCheckedChange={(v) => setForm({ ...form, is_emergency_contact: v })} /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Link</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (<div className={"space-y-1.5 " + (className ?? "")}><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>);
}
